import React from 'react';
import { PageTopBreadcrumb } from '../../../core/components/types';
import PageTop from '../../../core/components/PageTop';
import { ROUTE_LIST } from '../vendor_const';
import { useVendorEditInit } from './pagehooks/useVendorEditInit';
import { useVendorEditStore } from './pagehooks/useVendorEditStore';
import { useVendorEditModel } from './pagehooks/useVendorEditModel';
import FormEdit from '../../../core/components/form/FormEdit';
import FormGroup from '../../../core/components/form/FormGroup';
import ValidInputRadioText from '../../../core/components/validation/ValidInputRadioText';
import ValidInputDroplistText from '../../../core/components/validation/ValidInputDroplistText';
import ValidInputText from '../../../core/components/validation/ValidInputText';
import ValidMultiFieldText from '../../../core/components/validation/ValidMultiFieldText';
import Panel from '../../../core/components/Panel';
import InfoTooltip from '../../../core/components/InfoTooltip';
import FormSubmit from '../../../core/components/form/FormSubmit';
import Button from '../../../core/components/Button';
import ValidInputLabel from '../../../core/components/validation/ValidInputLabel';
import ScrollableListBox from '../../../core/components/ScrollableListBox';
import PharmacyListItem from './PharmacyListItem';
import { VendorType } from './vendor_edit_types';
import { AdminVendorModel } from '../vendor_types';
import useNgContext from '../../../core/services/useNgContext';

const TEXT_INPUT_WIDTH = 260;

const PageAdminVendorEdit = (): JSX.Element => {
    const title = useVendorEditInit();
    const breadcrumb: PageTopBreadcrumb = {
        title: 'Return to Vendor List',
        icon: 'sprite-ops',
        route: ROUTE_LIST,
    };

    return (
        <>
            <PageTop
                title={title}
                icon="sprite-ops"
                breadcrumb={breadcrumb}
            />
            <div className="vendor-feature vendor-edit">
                <Content />
            </div>
        </>
    );
};

const Content = (): JSX.Element => {
    const ctx = useNgContext();
    const isVerity = ctx.User?.Type === 'VERITY';
    const { state, cancelEdit } = useVendorEditStore();
    const { model, setPropText, setPropList, submit } = useVendorEditModel();

    // quick context helpers (moved ngContext() to init, its only needed once)
    const isUPDATE = state.mode == 'UPDATE';
    const isVeritySA = state.isVeritySA;
    const disablePBMorEDI = isDisablePBMorEDI(isVeritySA, isUPDATE, model);

    // quick vendor logic
    const vendorType = model.Type?.val as VendorType;
    const isPBM = vendorType === VendorType.PBM;
    const isTPA = vendorType === VendorType.TPA;
    const isWholesaler = vendorType === VendorType.WHOLESALER;
    const wholesalerRestriction =
        isVerity && isWholesaler && model.SupportsEDI?.val === 'true' && state.mode === 'UPDATE';

    const PBMRestriction = isVerity && isPBM && state.mode === 'UPDATE';
    const EDIOrPBMIsRestricted = wholesalerRestriction || PBMRestriction;

    if (state.init) {
        return <></>;
    }

    return (
        <Panel flexDirection="row">
            <FormEdit formName="vendorEditCreate">
                {wholesalerRestriction ? (
                    <i>Wholesaler Vendors that support EDI are Read Only for Verity users.</i>
                ) : null}
                {PBMRestriction ? <i>PBM Vendors are Read Only for Verity users.</i> : null}
                {!isTPA ? (
                    <FormGroup>
                        <ValidInputRadioText
                            prop={model.ObjectState}
                            alternateLabel="Status"
                            onChange={setPropText}
                            disabled={disablePBMorEDI}
                        />
                    </FormGroup>
                ) : null}
                <AccountFormGroup />
                {!isTPA ? <EdiFormGroup /> : null}
                {isWholesaler ? (
                    <FormGroup title="Wholesaler Settings">
                        <ValidMultiFieldText
                            prop={model.WholesalerExcludedVINs}
                            onChange={setPropList}
                            minlen={2}
                            maxlen={20}
                            disabled={!isVeritySA}
                        />
                    </FormGroup>
                ) : null}
                {isPBM ? (
                    <FormGroup title="PBM Settings">
                        <ValidInputDroplistText
                            prop={model.PBMExtractInterval}
                            onChange={setPropText}
                            disabled={!isVeritySA}
                        />
                        <ValidInputDroplistText
                            prop={model.PBMBenefitLevel}
                            onChange={setPropText}
                            disabled={!isVeritySA}
                        />
                        <ValidMultiFieldText
                            prop={model.PBMBINItems}
                            onChange={setPropList}
                            minlen={2}
                            maxlen={10}
                            disabled={!isVeritySA}
                        />
                    </FormGroup>
                ) : null}
                {isTPA ? <TpaPartnerAndSecurityFormGroup /> : null}
                {EDIOrPBMIsRestricted ? null : (
                    <FormSubmit lbl="Apply Changes">
                        <Button
                            onClick={submit}
                            disabled={state.waiting}
                        >
                            {state.mode}
                        </Button>
                        <span>
                            <Button
                                cssName="btn-cancel"
                                onClick={cancelEdit}
                            >
                                CANCEL
                            </Button>
                        </span>
                    </FormSubmit>
                )}
            </FormEdit>
        </Panel>
    );
};

// FORM GROUPS
/*
    AccountFormGroup
    TpaPartnerAndSecurityFormGroup
    EdiFormGroup
*/

function isDisablePBMorEDI(isVeritySA: boolean, isUPDATE: boolean, m: AdminVendorModel): boolean {
    return isUPDATE && !isVeritySA && (m.SupportsEDI?.val == 'true' || m.Type?.val === VendorType.PBM);
}

const AccountFormGroup = (): JSX.Element => {
    const { state } = useVendorEditStore();
    const { model, setPropText } = useVendorEditModel();

    const isVeritySA = state.isVeritySA;
    const isUPDATE = state.mode == 'UPDATE';
    const disablePBMorEDI = isDisablePBMorEDI(!!isVeritySA, isUPDATE, model);

    return (
        <FormGroup title="Account">
            <div className="vendor-edit-name-field">
                <ValidInputText
                    prop={model.Name}
                    alternateLabel="Name"
                    placeholder="Name"
                    onChange={setPropText}
                    width={TEXT_INPUT_WIDTH}
                    maxlen={50}
                    disabled={disablePBMorEDI}
                />
                <div className={`name-tooltip-container${model.Name.err ? ' has-error' : ''}`}>
                    <InfoTooltip
                        title="Name"
                        message={`This name is displayed within the UI to support a TPA's name change (e.g. Acquisition, Rebranding).`}
                        direction="right"
                    />
                </div>
            </div>
            <ValidInputText
                prop={model.Id}
                alternateLabel="Verity VENID"
                placeholder="Verity VENID"
                onChange={setPropText}
                width={TEXT_INPUT_WIDTH}
                maxlen={15}
                disabled={isUPDATE}
            />
            {/*
                where does this happen to constrain Type options?
                <option class="ng-hide" ng-show="vm.isVeritySA" value="PBM">PBM</option>
                <option class="ng-hide" ng-show="vm.isVeritySA" value="TPA">TPA</option>

            */}
            <ValidInputDroplistText
                prop={model.Type}
                onChange={setPropText}
                disabled={isUPDATE}
            />
        </FormGroup>
    );
};

const TpaPartnerAndSecurityFormGroup = (): JSX.Element => {
    const { state } = useVendorEditStore();
    const { model, setPropText } = useVendorEditModel();

    const isVeritySA = state.isVeritySA;
    const isUPDATE = state.mode == 'UPDATE';

    const pharmacies = model.TPAPharmacies.val.map((pharmacy: string) => ({
        id: pharmacy,
        name: pharmacy,
    }));

    return (
        <>
            {isUPDATE ? (
                <FormGroup title="TPA Partner">
                    <div className="component-valid-input">
                        <div className="input-wrapper">
                            <ValidInputLabel label="Pharmacies" />
                            <div className="vendor-edit-pharmacies-field">
                                <p className="pharmacies-subtitle">({pharmacies.length} items)</p>
                                {pharmacies.length > 0 ? (
                                    <ScrollableListBox
                                        items={pharmacies}
                                        listItemRenderer={(item: Record<string, any>, key: number) => (
                                            <PharmacyListItem
                                                key={key}
                                                item={item}
                                            />
                                        )}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </FormGroup>
            ) : null}
            <FormGroup title="Security">
                <div className="component-valid-input vendor-public-key-field">
                    <div className="input-wrapper">
                        <ValidInputLabel label="Public Key" />
                        <InfoTooltip
                            title="Upload File Encryption Public Key"
                            message="Only available to VeritySA users."
                            direction="right"
                        >
                            <textarea
                                className="public-key-textarea"
                                value={model.KeyStr.val}
                                onChange={e => setPropText('KeyStr', e.target.value)}
                                placeholder="Public Key"
                                rows={5}
                                cols={50}
                                disabled={!isVeritySA}
                            />
                        </InfoTooltip>
                    </div>
                    {model.KeyStr.err ? <span className="vendor-edit-public-key-error">{model.KeyStr.err}</span> : null}
                </div>
            </FormGroup>
        </>
    );
};

const EdiFormGroup = (): JSX.Element => {
    const { state } = useVendorEditStore();
    const { model, setPropText } = useVendorEditModel();

    const isVeritySA = state.isVeritySA;
    const isPBM = model.Type?.val === VendorType.PBM;

    // convert string bools into bool
    const hasEDI = model.SupportsEDI?.val == 'true';
    const hasCreditReplenish = model.CreditReplenish?.val == 'true';
    const hasCSOSplit = model.CSOSSplit?.val == 'true';

    // tool tips
    const tooltipCreditReplenish = 'Vendor-provided Receiver ID for Credit-based Replenishment orders';

    return (
        <FormGroup title="EDI Settings">
            <ValidInputRadioText
                prop={model.SupportsEDI}
                alternateLabel="Supports EDI"
                onChange={setPropText}
                disabled={isPBM || !isVeritySA}
            />
            {hasEDI ? (
                <>
                    <ValidInputDroplistText
                        prop={model.ProxyVendor}
                        onChange={setPropText}
                    />
                    <ValidInputText
                        prop={model.SenderId}
                        alternateLabel="Sender ID"
                        placeholder="Sender ID"
                        onChange={setPropText}
                        width={TEXT_INPUT_WIDTH}
                        disabled={!isVeritySA}
                    />
                    <ValidInputText
                        prop={model.ReceiverId}
                        alternateLabel="Receiver ID"
                        placeholder="Receiver ID"
                        onChange={setPropText}
                        width={TEXT_INPUT_WIDTH}
                        disabled={!isVeritySA}
                    />
                    <ValidInputRadioText
                        prop={model.ShortPONames}
                        alternateLabel="Short PO Names"
                        onChange={setPropText}
                        disabled={!isVeritySA}
                    />
                    {/* CSOS Split */}
                    <div className="fieldset-row">
                        <ValidInputRadioText
                            prop={model.CSOSSplit}
                            alternateLabel="CSOS Split"
                            onChange={setPropText}
                            disabled={!isVeritySA}
                        />
                        {hasCSOSplit ? (
                            <>
                                <ValidInputText
                                    prop={model.CSOSReceiverId}
                                    alternateLabel="CSOS Receiver ID"
                                    placeholder="Receiver ID"
                                    onChange={setPropText}
                                    disabled={!isVeritySA}
                                />
                                <ValidInputText
                                    prop={model.CSOSSenderId}
                                    alternateLabel="CSOS Sender ID"
                                    placeholder="Sender ID"
                                    onChange={setPropText}
                                    disabled={!isVeritySA}
                                />
                            </>
                        ) : null}
                    </div>
                    {/* Credit Replenish */}
                    <div className="fieldset-row">
                        <ValidInputRadioText
                            prop={model.CreditReplenish}
                            alternateLabel="Credit-Based Replenishment"
                            onChange={setPropText}
                            disabled={!isVeritySA}
                        />
                        {hasCreditReplenish ? (
                            <InfoTooltip
                                message={tooltipCreditReplenish}
                                direction="right"
                            >
                                <ValidInputText
                                    prop={model.CreditReplenishId}
                                    alternateLabel="Receiver ID"
                                    placeholder="Receiver ID"
                                    onChange={setPropText}
                                    disabled={!isVeritySA}
                                />
                            </InfoTooltip>
                        ) : null}
                    </div>
                </>
            ) : null}
        </FormGroup>
    );
};

export default PageAdminVendorEdit;
